import { BrandKicker, PulseTrace } from '../../components/brand';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import Seo from '../../components/seo/Seo';
import {
  Layout,
  HeroSection,
  ServicesHeroTitle,
  HeroSubtitle,
  PageContainer,
  Section,
  SectionTitle,
  SectionText,
  ServicesGrid,
  ServiceCard,
  CardTitle,
  CardTagline,
  CardDescription,
  CardLink,
  CtaSection,
  CtaTitle,
  CtaText,
  ButtonRow,
  PrimaryButton,
  FaqSection,
  FaqItem,
  FaqQuestion,
  FaqAnswer,
} from './Style';
import {
  servicesConfig,
  getServicePath,
  getServicesPath,
} from './servicesConfig';

type FaqEntry = {
  question: string;
  answer: string;
};

const ServicesPage = () => {
  const { t, i18n } = useTranslation('services');
  const lang = i18n.language?.startsWith('fr') ? 'fr' : 'en';
  const servicesPath = getServicesPath(lang);
  const siteUrl =
    (import.meta as any).env?.VITE_SITE_URL || 'https://sor-seo.com';
  const homePath = lang === 'fr' ? '/fr' : '/';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [lang]);

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'SOR SEO',
    url: siteUrl,
    logo: `${siteUrl}/logoSORBlack.svg`,
    sameAs: ['https://www.linkedin.com/company/sor-seo'],
  };

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: t('hero.title'),
    description: t('intro.paragraph'),
    provider: {
      '@type': 'Organization',
      name: 'SOR SEO',
      url: siteUrl,
    },
    serviceType: servicesConfig.map((service) =>
      t(`cards.${service.id}.title`),
    ),
    areaServed: 'CA',
    url: `${siteUrl}${servicesPath}`,
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: t('navigation.homeLabel'),
        item: `${siteUrl}${homePath}`,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: t('hero.title'),
        item: `${siteUrl}${servicesPath}`,
      },
    ],
  };

  const faqItems = t('faq.items', { returnObjects: true });
  const normalizedFaqItems = Array.isArray(faqItems)
    ? (faqItems as FaqEntry[])
    : [];

  return (
    <Layout>
      <Seo
        title={t('seo.title')}
        description={t('seo.description')}
        canonicalPath={servicesPath}
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>
      <HeroSection>
        <BrandKicker>
          {lang === 'fr' ? 'L’ambition, concrétisée' : 'Ambition, engineered'}
        </BrandKicker>
        <ServicesHeroTitle>
          <strong>{t('hero.title').split('\n')[0]}</strong>{' '}
          <span>{t('hero.title').split('\n').slice(1).join(' ')}</span>
        </ServicesHeroTitle>
        <HeroSubtitle>{t('hero.subtitle')}</HeroSubtitle>
        <PulseTrace />
      </HeroSection>
      <PageContainer>
        <Section>
          <SectionTitle>{t('intro.title')}</SectionTitle>
          <SectionText>{t('intro.paragraph')}</SectionText>
        </Section>

        <Section>
          <SectionTitle>{t('servicesList.title')}</SectionTitle>
          <SectionText>{t('servicesList.subtitle')}</SectionText>
          <ServicesGrid>
            {servicesConfig.map((service) => (
              <ServiceCard key={service.id}>
                <CardTitle>{t(`cards.${service.id}.title`)}</CardTitle>
                <CardTagline>{t(`cards.${service.id}.tagline`)}</CardTagline>
                <CardDescription>
                  {t(`cards.${service.id}.description`)}
                </CardDescription>
                <CardLink to={getServicePath(lang, service.slugs[lang])}>
                  {t('cards.linkLabel')}
                </CardLink>
              </ServiceCard>
            ))}
          </ServicesGrid>
        </Section>

        <CtaSection>
          <CtaTitle>{t('cta.title')}</CtaTitle>
          <CtaText>{t('cta.description')}</CtaText>
          <ButtonRow>
            <PrimaryButton to={`/${lang}/contact`}>
              {t('cta.primaryLabel')}
            </PrimaryButton>
          </ButtonRow>
        </CtaSection>

        {normalizedFaqItems.length > 0 ? (
          <Section>
            <SectionTitle>{t('faq.title')}</SectionTitle>
            <FaqSection>
              {normalizedFaqItems.map((item) => (
                <FaqItem key={item.question}>
                  <FaqQuestion>{item.question}</FaqQuestion>
                  <FaqAnswer>{item.answer}</FaqAnswer>
                </FaqItem>
              ))}
            </FaqSection>
          </Section>
        ) : null}
      </PageContainer>
    </Layout>
  );
};

export default ServicesPage;
